import { usePropertyForm, usePropertyQuery } from "./hooks";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link, useLocation, Routes, Route } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OverviewTab, LeasesTab, DocumentsTab, ServiceRequestsTab } from "./routes";
import { PropertyHeader } from "@/components/shared/PropertyHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const TABS = {
  OVERVIEW: "overview",
  LEASES: "leases",
  DOCUMENTS: "documents",
  SERVICE_REQUESTS: "service-requests"
};

const PROPERTY_TABS = [
  { id: TABS.OVERVIEW, path: "", translationKey: "properties.tabs.overview", fallback: "Огляд" },
  { id: TABS.LEASES, path: "/leases", translationKey: "properties.tabs.leases", fallback: "Оренда" },
  { id: TABS.DOCUMENTS, path: "/documents", translationKey: "properties.tabs.documents", fallback: "Документи" },
  { id: TABS.SERVICE_REQUESTS, path: "/service-requests", translationKey: "properties.tabs.service-requests", fallback: "Заяви на сервісне обслуговування" }
];

// Основний компонент для сторінки властивості
const PropertyPage = () => {
  const { t } = useTranslation();
  const { propertyUUID } = useParams<{ propertyUUID: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const {
    property,
    isLoading,
    updateProperty,
    isUpdating,
    uploadImages,
    isUploading,
    reorderImages,
    isReordering,
    deleteImage,
    isDeleting,
    deleteAllImages,
    isDeletingAll,
  } = usePropertyQuery(propertyUUID!);

  const form = usePropertyForm(property);

  // Визначаємо активний таб на основі поточного URL
  const currentPath = location.pathname;
  const activeTab = currentPath.includes("/leases")
    ? TABS.LEASES
    : currentPath.includes("/documents")
      ? TABS.DOCUMENTS
      : currentPath.includes("/service-requests")
        ? TABS.SERVICE_REQUESTS
        : TABS.OVERVIEW;

  const handleBack = () => {
    navigate('/home/properties');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>{t("properties.errors.notFound")}</p>
      </div>
    );
  }

  // Функція для рендеру активної вкладки
  const renderActiveTab = () => {
    switch (activeTab) {
      case TABS.OVERVIEW:
        return (
          <OverviewTab
            property={property}
            form={form}
            updateProperty={updateProperty}
            isUpdating={isUpdating}
            uploadImages={uploadImages}
            reorderImages={reorderImages}
            deleteImage={deleteImage}
            deleteAllImages={deleteAllImages}
            isUploading={isUploading}
            isDeleting={isDeleting}
            isDeletingAll={isDeletingAll}
          />
        );
      case TABS.LEASES:
        return <LeasesTab property={property} />;
      case TABS.DOCUMENTS:
        return <DocumentsTab property={property} />;
      case TABS.SERVICE_REQUESTS:
        return <ServiceRequestsTab property={property} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container py-8 space-y-8">
        <PropertyHeader
          property={property}
          onBack={handleBack}
        />

        <Tabs value={activeTab} className="w-full">
          <TabsList className="mb-8">
            {PROPERTY_TABS.map((tab) => (
              <Link key={tab.id} to={`/home/properties/${propertyUUID}${tab.path}`}>
                <TabsTrigger value={tab.id} className="cursor-pointer">
                  {t(tab.translationKey) || tab.fallback}
                </TabsTrigger>
              </Link>
            ))}
          </TabsList>

          {renderActiveTab()}
        </Tabs>
      </div>
    </div>
  );
};

export default PropertyPage;
